// Resumable signed upload to Supabase Storage via the TUS protocol (P0-UP-02 / CAP-0.x).
//
// Thin wrapper over tus-js-client@4 targeting Supabase Storage's resumable endpoint. A stopped or
// killed upload resumes from the server's `Upload-Offset` (tus-js-client re-discovers the prior
// upload via its fingerprint URL storage and PATCHes from the accepted offset), so a flaky mobile
// connection never restarts a large audio file from zero.
//
// This is the SHARED native substrate the capture loop imports — there is no duplicate in
// src/lib/capture/. It works on web too (tus-js-client is isomorphic); the website is unaffected
// because nothing on the website calls it.
//
// Auth: the caller's Supabase access token (Authorization: Bearer …). `x-upsert: false` so a
// re-upload to an existing object errors rather than clobbering it (idempotency is the caller's job
// via a stable objectPath). Chunk size 6 MB (Supabase resumable uploads require a fixed 6 MB chunk).

import { Upload } from 'tus-js-client';
import { supabase } from '@/integrations/supabase/client';

// Supabase Storage resumable (TUS) endpoint for project ewyfhrjexfrmcjhqzsmm.
const TUS_ENDPOINT = 'https://ewyfhrjexfrmcjhqzsmm.supabase.co/storage/v1/upload/resumable';
const DEFAULT_BUCKET = 'research-uploads';
// Supabase requires exactly 6 MB chunks for resumable uploads.
const CHUNK_SIZE = 6 * 1024 * 1024;

export interface UploadResumableParams {
  /** The bytes to upload. */
  data: File | Blob;
  /** Object path within the target bucket (e.g. `captures/<uuid>.m4a`). */
  objectPath: string;
  /**
   * Target bucket. Optional — defaults to `research-uploads` so every existing caller is
   * byte-for-byte unaffected. Podcast publish (Admin upload page) passes `podcast-src` /
   * `podcast-media` here.
   */
  bucket?: string;
  /** Content type; falls back to the blob's own type or application/octet-stream. */
  contentType?: string;
  /** Progress callback: (bytesSent, bytesTotal). */
  onProgress?: (bytesSent: number, bytesTotal: number) => void;
  /**
   * Optional AbortSignal — aborting pauses the upload WITHOUT terminating the server-side draft, so
   * a later call with the same objectPath resumes from the saved offset.
   */
  signal?: AbortSignal;
}

export interface UploadResumableResult {
  objectPath: string;
  bucket: string;
  /** The TUS upload URL (useful for resuming / diagnostics). */
  uploadUrl: string | null;
}

/**
 * Upload `data` to `research-uploads/objectPath` resumably. Resolves when the upload completes,
 * rejects on unrecoverable error (after the retry schedule is exhausted) or on abort.
 */
export async function uploadResumable(
  params: UploadResumableParams,
): Promise<UploadResumableResult> {
  const { data, objectPath, bucket, contentType, onProgress, signal } = params;
  const resolvedBucket = bucket || DEFAULT_BUCKET;

  if (signal?.aborted) {
    throw new DOMException('Upload aborted before start', 'AbortError');
  }

  // Fresh token at call time so a long-pending queued item uploads with a valid session.
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new Error('resumableUpload: no Supabase session — sign in before uploading.');
  }

  const resolvedContentType = contentType || data.type || 'application/octet-stream';

  return new Promise<UploadResumableResult>((resolve, reject) => {
    const upload = new Upload(data, {
      endpoint: TUS_ENDPOINT,
      // Retry on transient failures with exponential-ish backoff (ms). null would disable retries.
      retryDelays: [0, 1000, 3000, 5000, 10000, 20000],
      chunkSize: CHUNK_SIZE,
      headers: {
        authorization: `Bearer ${accessToken}`,
        // Never overwrite an existing object — caller owns idempotency via a stable objectPath.
        'x-upsert': 'false',
      },
      // Supabase keys the upload off these metadata fields.
      metadata: {
        bucketName: resolvedBucket,
        objectName: objectPath,
        contentType: resolvedContentType,
        cacheControl: '3600',
      },
      // Allow resume: persist the fingerprint→URL mapping (default urlStorage) so
      // findPreviousUploads() can pick the in-progress upload back up on a later call.
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      onError: (error) => {
        cleanup();
        reject(error);
      },
      onProgress: (bytesSent, bytesTotal) => {
        onProgress?.(bytesSent, bytesTotal);
      },
      onSuccess: () => {
        cleanup();
        resolve({ objectPath, bucket: resolvedBucket, uploadUrl: upload.url });
      },
      // Retry on network/5xx; do not retry on auth/precondition failures (e.g. 401/403/409/412).
      onShouldRetry: (err) => {
        const status = err?.originalResponse?.getStatus?.();
        if (status === 401 || status === 403 || status === 409 || status === 412) return false;
        return true;
      },
    });

    function cleanup() {
      if (signal) signal.removeEventListener('abort', onAbort);
    }

    function onAbort() {
      // Pause (do NOT terminate) so the partial upload can resume later from Upload-Offset.
      void upload.abort(false);
      cleanup();
      reject(new DOMException('Upload aborted', 'AbortError'));
    }

    if (signal) signal.addEventListener('abort', onAbort, { once: true });

    // Resume from a previous attempt if one exists for this fingerprint (same file + endpoint).
    upload
      .findPreviousUploads()
      .then((previous) => {
        if (previous.length > 0) {
          upload.resumeFromPreviousUpload(previous[0]);
        }
        upload.start();
      })
      .catch(() => {
        // If resume discovery fails, start fresh rather than blocking the upload.
        upload.start();
      });
  });
}
